// Analytics — tabbed view over the same contactAIAgentSession window.
//
// Tabs:
//   - Overview   — single-screen snapshot (outcome mix, concern mix,
//                  daily volume, top entry points, top agents)
//   - Business   — Queue health + Bot effectiveness scorecards +
//                  Bot effectiveness by day + Booking activity
//   - Technical  — Caller journey funnel + Handoff sink + Routing flow +
//                  Call duration vs. handoff + Bot-tool invocation audit
//
// Date-range form is shared across tabs — fetchWindow() hits
// run-search-query for sessions + taskDetails + taskLegDetails once,
// and every tab projects its view from the same base dataset.
// Switching tabs is instant; only a date-range change re-fetches.
//
// Column visibility per report persists in sessionStorage (see
// ./reports/columnPrefs.js). Window state persists in the URL query
// string so a supervisor can bookmark/share a specific view.
//
// Each report is wrapped in a <ReportBoundary> so a render-time crash
// in one report doesn't blank the whole page.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Grid,
  Group,
  Loader,
  MultiSelect,
  Paper,
  Stack,
  Tabs,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconAlertTriangle, IconChartBar, IconFilter, IconHammer, IconInfoCircle, IconRefresh } from '@tabler/icons-react';

import { fmtTs, toDatetimeLocal } from './utils';
import { useReportWindow } from './reports/useReportWindow';
import { AnalyticsOverview } from './reports/AnalyticsOverview';
import { QueueHealth } from './reports/QueueHealth';
import { BotEffectiveness } from './reports/BotEffectiveness';
import { BotEffectivenessByDay } from './reports/BotEffectivenessByDay';
import { FlowFunnel } from './reports/FlowFunnel';
import { HandoffSinkAnalysis } from './reports/HandoffSinkAnalysis';
import { BookingLeadTime } from './reports/BookingLeadTime';
import { CallDurationVsHandoff } from './reports/CallDurationVsHandoff';
import { RoutingFlow } from './reports/RoutingFlow';
import { SpecVsReality } from './reports/SpecVsReality';
import { BusinessOutcomes } from './reports/BusinessOutcomes';
import { BotToolAudit } from './reports/BotToolAudit';
import { EarlyAbandon } from './reports/EarlyAbandon';
import { EpicErrorDetector } from './reports/EpicErrorDetector';
import { PrematureHandover } from './reports/PrematureHandover';
import { ReportBoundary } from './components/ReportBoundary';
import { DateRangePresets } from './components/DateRangePresets';
import { analyzeWindow } from './lib/sessionClassification';

const LOW_SAMPLE_THRESHOLD = 30;

export function Analytics({ setView }) {
  const nowIso = toDatetimeLocal(new Date());
  const dayAgoIso = toDatetimeLocal(new Date(Date.now() - 24 * 60 * 60 * 1000));

  // Hydrate from URL query params on mount (?from=...&to=... in datetime-local
  // ISO format). Fall back to the "last 24h" default.
  const initialForm = useMemo(() => {
    const params = parseQuery();
    return {
      from: params.from || dayAgoIso,
      to:   params.to   || nowIso,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount only

  const [form, setForm] = useState(initialForm);
  const [tab, setTab] = useState(() => parseQuery().tab || 'overview');
  // botFilter: array of INCLUDED bot names (resolved to the agentName value a
  // session carries, or EMPTY_LABEL for sessions with no name). `null` means
  // "haven't initialised yet" — on first data arrival we'll seed it to all
  // discovered bots so the MultiSelect reads as "everything selected".
  const [botFilter, setBotFilter] = useState(() => parseQuery().bots);
  const { loading, data, error, fetchedAt, from, to, fetchWindow, cancel, progress } = useReportWindow();

  // Mantine 8 TextInput onChange with type="datetime-local" occasionally
  // nullifies `currentTarget` by the time the handler fires (observed in
  // prod on dev-presidio-cc-tools 2026-10-21). Pull the value defensively
  // and bail if it isn't readable — never crash the whole Analytics tree.
  const updateFormField = useCallback((field) => (event) => {
    const value =
      event?.currentTarget?.value ??
      event?.target?.value ??
      null;
    if (value == null) return;
    setForm((f) => ({ ...f, [field]: value }));
  }, []);

  const runFetch = useCallback(() => {
    const fromMs = new Date(form.from).getTime();
    const toMs = new Date(form.to).getTime();
    if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || fromMs >= toMs) return;
    fetchWindow(fromMs, toMs);
  }, [form, fetchWindow]);

  // Auto-fetch on first mount if URL carried a window — otherwise wait for
  // the user to pick a window and click Compute (prevents an unwanted fetch
  // just because someone opened the page).
  useEffect(() => {
    const params = parseQuery();
    if (params.from && params.to) runFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Distinct bot names observed in the fetched window. Empty-agentName sessions
  // collapse to EMPTY_LABEL so they're still a toggle-able filter option.
  const EMPTY_LABEL = '(no agentName)';
  const discoveredBots = useMemo(() => {
    if (!data?.sessions) return [];
    const set = new Set();
    for (const s of data.sessions) set.add(s.agentName || EMPTY_LABEL);
    return [...set].sort();
  }, [data]);

  // Seed the filter on first data arrival (if the user hadn't already picked
  // bots via the URL). Afterwards leave it alone — if a new window drops a
  // previously-selected bot, the MultiSelect just shows the stale value with
  // a dim marker; the filter still works because unknown values match nothing.
  useEffect(() => {
    if (!data) return;
    if (botFilter === null) setBotFilter(discoveredBots);
  }, [data, discoveredBots, botFilter]);

  // Apply the bot filter to sessions BEFORE every report sees the data. Tasks
  // and legs stay unfiltered — they key off contactId, and QueueHealth /
  // FlowFunnel use them independently of agentName.
  const filteredData = useMemo(() => {
    if (!data) return null;
    const sel = Array.isArray(botFilter) ? botFilter : null;
    if (!sel || sel.length === 0 || sel.length === discoveredBots.length) return data;
    const selSet = new Set(sel);
    const filteredSessions = data.sessions.filter(
      (s) => selSet.has(s.agentName || EMPTY_LABEL),
    );
    return { ...data, sessions: filteredSessions };
  }, [data, botFilter, discoveredBots]);

  const filterActive = Array.isArray(botFilter)
    && botFilter.length > 0
    && botFilter.length !== discoveredBots.length;

  const applyBotQuickPick = useCallback((predicate) => {
    setBotFilter(discoveredBots.filter(predicate));
  }, [discoveredBots]);

  // Keep URL in sync with the form + active tab + bot filter so share links
  // roundtrip. Only write ?bots= when the filter actively narrows the view;
  // "all selected" leaves the URL clean.
  useEffect(() => {
    writeQuery({
      from: form.from,
      to: form.to,
      tab,
      bots: filterActive ? botFilter : null,
    });
  }, [form.from, form.to, tab, botFilter, filterActive]);

  const applyPreset = useCallback((fromStr, toStr) => {
    setForm({ from: fromStr, to: toStr });
    // Also fire the fetch immediately — presets are "intended to run" clicks.
    const fromMs = new Date(fromStr).getTime();
    const toMs = new Date(toStr).getTime();
    if (Number.isFinite(fromMs) && Number.isFinite(toMs) && fromMs < toMs) {
      fetchWindow(fromMs, toMs);
    }
  }, [fetchWindow]);

  // Classify ONCE here, prop-drill to every report that needs it. Prevents
  // BotEffectiveness + BotEffectivenessByDay + RoutingFlow from each
  // rebuilding the sibling-session map on every re-render. Classification
  // works off the FILTERED sessions so bot-level stats reflect the active
  // bot selection.
  const analysis = useMemo(
    () => (filteredData ? analyzeWindow(filteredData.sessions, filteredData.tasks) : null),
    [filteredData],
  );

  // SessionListModal navigation callback. Reports pass this into the modal
  // so clicking a session row jumps into Call Detail with the right window
  // context pre-populated.
  const handleViewSession = useCallback((interactionId, viewWindow) => {
    if (!setView || !interactionId) return;
    const effectiveWindow = viewWindow ?? (from && to ? { from, to } : null);
    setView({
      name: 'call-detail',
      interactionId,
      window: effectiveWindow,
      cachedAiSessions: data?.sessions ?? null,
    });
  }, [setView, from, to, data]);

  const totalSessionCount = data?.sessions?.length ?? 0;
  const sessionCount = filteredData?.sessions?.length ?? 0;
  const showLowSampleBanner = !loading && filteredData && sessionCount > 0 && sessionCount < LOW_SAMPLE_THRESHOLD;

  return (
    <Stack>
      <div>
        <Title order={3}>Analytics</Title>
        <Text size="sm" c="dimmed">
          Session activity snapshot and operational reports. Pick a window, then switch tabs — the
          data is fetched once and projected into every view. Business tab is for supervisors;
          Technical tab is for engineers investigating bot behaviour.
        </Text>
      </div>

      <Paper withBorder p="md" radius="md">
        <Grid gutter="sm" align="flex-end">
          <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
            <TextInput
              label="From"
              type="datetime-local"
              value={form.from}
              onChange={updateFormField('from')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
            <TextInput
              label="To"
              type="datetime-local"
              value={form.to}
              onChange={updateFormField('to')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Group gap="xs">
              <Button
                size="xs"
                leftSection={<IconRefresh size={14} />}
                loading={loading}
                onClick={runFetch}
              >
                Compute
              </Button>
              <DateRangePresets onPick={applyPreset} loading={loading} />
              {fetchedAt && (
                <Text size="xs" c="dimmed">
                  {filterActive ? `${sessionCount} of ${totalSessionCount}` : sessionCount} sessions
                  · {data?.legs.length ?? 0} legs · {fmtTs(fetchedAt, true)}
                </Text>
              )}
            </Group>
          </Grid.Col>
          <Grid.Col span={12}>
            <MultiSelect
              label={
                <Group gap={6} align="center">
                  <IconFilter size={14} />
                  <Text size="sm" fw={500}>Bots</Text>
                  {filterActive && (
                    <Badge size="xs" variant="light" color="blue">
                      {botFilter.length} of {discoveredBots.length}
                    </Badge>
                  )}
                </Group>
              }
              placeholder={discoveredBots.length === 0
                ? 'Run Compute to populate bot list'
                : 'All bots selected'}
              data={discoveredBots}
              value={Array.isArray(botFilter) ? botFilter : []}
              onChange={setBotFilter}
              disabled={discoveredBots.length === 0}
              searchable
              clearable
              hidePickedOptions
              maxDropdownHeight={240}
              description="Filters every report below. Reports that key off bot name (BotEffectiveness, BusinessOutcomes, EpicErrorDetector, …) reflect the current selection; QueueHealth and FlowFunnel also pick up the narrower session set."
            />
            {discoveredBots.length > 0 && (
              <Group gap={6} mt={6}>
                <Text size="xs" c="dimmed">Quick picks:</Text>
                <Button
                  size="compact-xs"
                  variant="subtle"
                  onClick={() => applyBotQuickPick((n) => n.startsWith('PROD-'))}
                >
                  PROD only
                </Button>
                <Button
                  size="compact-xs"
                  variant="subtle"
                  onClick={() => applyBotQuickPick((n) => n.startsWith('TESTPROD-'))}
                >
                  TESTPROD only
                </Button>
                <Button
                  size="compact-xs"
                  variant="subtle"
                  onClick={() => applyBotQuickPick((n) => n.startsWith('PROD-') || n.startsWith('TESTPROD-'))}
                >
                  PROD + TESTPROD
                </Button>
                <Button
                  size="compact-xs"
                  variant="subtle"
                  onClick={() => setBotFilter(discoveredBots)}
                >
                  All
                </Button>
              </Group>
            )}
          </Grid.Col>
        </Grid>
      </Paper>

      {error && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />} title="Fetch failed">
          <Text size="xs">{error}</Text>
        </Alert>
      )}
      {loading && (
        <Paper withBorder p="sm" radius="md">
          <Group justify="space-between" mb={6}>
            <Group gap="xs">
              <Loader size="xs" />
              <Text size="xs" fw={500}>Pulling full-window data (adaptive slicing)…</Text>
            </Group>
            <Button size="xs" variant="subtle" color="red" onClick={cancel}>Cancel</Button>
          </Group>
          {(['sessions', 'tasks', 'legs']).map((bucket) => {
            const p = progress?.[bucket] ?? { phase: 'idle', slicesTotal: 0, bisections: 0, rowsLoaded: 0 };
            const label = bucket === 'sessions' ? 'AI sessions' : bucket === 'tasks' ? 'Tasks' : 'Legs';
            return (
              <Text key={bucket} size="xs" c="dimmed">
                {label}: {p.phase === 'idle' ? 'queued' : p.phase === 'done' ? 'done' : 'fetching'} ·
                {' '}{p.rowsLoaded.toLocaleString()} rows · {p.slicesTotal} slice{p.slicesTotal === 1 ? '' : 's'}
                {p.bisections > 0 ? ` · ${p.bisections} bisection${p.bisections === 1 ? '' : 's'}` : ''}
              </Text>
            );
          })}
        </Paper>
      )}

      {!loading && data && (
        <>
          {(data.sessionsTruncated || data.tasksTruncated || data.legsTruncated) && (
            <Alert color="orange" icon={<IconAlertTriangle size={16} />} title="Still truncated after adaptive slicing">
              At least one root hit the 1-minute slice floor with <strong>hasNextPage: true</strong>. This
              only happens with pathological volume (&gt;500 rows in a single minute). The numbers below are a
              partial slice — contact engineering if you see this.
            </Alert>
          )}
          <Alert color="gray" variant="light" icon={<IconInfoCircle size={14} />}>
            <Text size="xs">
              Fetched <strong>complete</strong> window via adaptive bisection ·
              {' '}sessions: {data.sessionsMeta?.slicesTotal ?? 0} slice{(data.sessionsMeta?.slicesTotal ?? 0) === 1 ? '' : 's'}
              {(data.sessionsMeta?.bisections ?? 0) > 0 ? ` (${data.sessionsMeta.bisections} bisection${data.sessionsMeta.bisections === 1 ? '' : 's'})` : ''}
              {' '}· tasks: {data.tasksMeta?.slicesTotal ?? 0} slice{(data.tasksMeta?.slicesTotal ?? 0) === 1 ? '' : 's'}
              {(data.tasksMeta?.bisections ?? 0) > 0 ? ` (${data.tasksMeta.bisections} bisection${data.tasksMeta.bisections === 1 ? '' : 's'})` : ''}
              {' '}· legs: {data.legsMeta?.slicesTotal ?? 0} slice{(data.legsMeta?.slicesTotal ?? 0) === 1 ? '' : 's'}
              {(data.legsMeta?.bisections ?? 0) > 0 ? ` (${data.legsMeta.bisections} bisection${data.legsMeta.bisections === 1 ? '' : 's'})` : ''}
            </Text>
          </Alert>

          {showLowSampleBanner && (
            <Alert color="yellow" icon={<IconInfoCircle size={16} />} title="Small sample">
              Only {sessionCount} session{sessionCount === 1 ? '' : 's'} in this window. Role classifications and
              success rates may be noisy below ~{LOW_SAMPLE_THRESHOLD} sessions — extend the range for more
              reliable rollups.
            </Alert>
          )}

          <Tabs value={tab} onChange={(v) => setTab(v ?? 'overview')} keepMounted={false}>
            <Tabs.List>
              <Tabs.Tab value="overview" leftSection={<IconChartBar size={14} />}>Overview</Tabs.Tab>
              <Tabs.Tab value="business" leftSection={<IconChartBar size={14} />}>Business reports</Tabs.Tab>
              <Tabs.Tab value="technical" leftSection={<IconHammer size={14} />}>Technical reports</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="overview" pt="md">
              <ReportBoundary name="AnalyticsOverview">
                <AnalyticsOverview sessions={filteredData.sessions} />
              </ReportBoundary>
            </Tabs.Panel>

            <Tabs.Panel value="business" pt="md">
              <Stack gap="md">
                <ReportBoundary name="BusinessOutcomes"><BusinessOutcomes data={filteredData} onViewSession={handleViewSession} window={from && to ? { from, to } : null} /></ReportBoundary>
                <ReportBoundary name="QueueHealth"><QueueHealth data={filteredData} /></ReportBoundary>
                <ReportBoundary name="BotEffectiveness"><BotEffectiveness data={filteredData} analysis={analysis} onViewSession={handleViewSession} window={from && to ? { from, to } : null} /></ReportBoundary>
                <ReportBoundary name="BotEffectivenessByDay"><BotEffectivenessByDay data={filteredData} analysis={analysis} onViewSession={handleViewSession} window={from && to ? { from, to } : null} /></ReportBoundary>
                <ReportBoundary name="BookingLeadTime"><BookingLeadTime data={filteredData} onViewSession={handleViewSession} window={from && to ? { from, to } : null} /></ReportBoundary>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="technical" pt="md">
              <Stack gap="md">
                <ReportBoundary name="FlowFunnel"><FlowFunnel data={filteredData} /></ReportBoundary>
                <ReportBoundary name="HandoffSinkAnalysis"><HandoffSinkAnalysis data={filteredData} /></ReportBoundary>
                <ReportBoundary name="RoutingFlow"><RoutingFlow data={filteredData} analysis={analysis} onViewSession={handleViewSession} window={from && to ? { from, to } : null} /></ReportBoundary>
                <ReportBoundary name="SpecVsReality"><SpecVsReality data={filteredData} /></ReportBoundary>
                <ReportBoundary name="EarlyAbandon"><EarlyAbandon data={filteredData} onViewSession={handleViewSession} window={from && to ? { from, to } : null} /></ReportBoundary>
                <ReportBoundary name="EpicErrorDetector"><EpicErrorDetector data={filteredData} onViewSession={handleViewSession} window={from && to ? { from, to } : null} /></ReportBoundary>
                <ReportBoundary name="PrematureHandover"><PrematureHandover data={filteredData} onViewSession={handleViewSession} window={from && to ? { from, to } : null} /></ReportBoundary>
                <ReportBoundary name="CallDurationVsHandoff"><CallDurationVsHandoff data={filteredData} /></ReportBoundary>
                <ReportBoundary name="BotToolAudit"><BotToolAudit from={from} to={to} /></ReportBoundary>
              </Stack>
            </Tabs.Panel>
          </Tabs>
        </>
      )}

      {!loading && !data && !error && (
        <Alert color="gray" variant="light">
          Pick a window above and hit <strong>Compute</strong> to populate the reports.
        </Alert>
      )}
    </Stack>
  );
}

// ---- URL query helpers ------------------------------------------------
// Analytics state → ?from=&to=&tab= so a supervisor can bookmark / share
// a specific view. Uses history.replaceState so navigation history isn't
// polluted with every tab switch.

function parseQuery() {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  const from = params.get('from');
  const to = params.get('to');
  const tab = params.get('tab');
  const botsRaw = params.get('bots');
  const bots = botsRaw
    ? botsRaw.split(',').map((s) => s.trim()).filter(Boolean)
    : null;
  return {
    from: from && isDatetimeLocal(from) ? from : null,
    to:   to   && isDatetimeLocal(to)   ? to   : null,
    tab:  tab && ['overview', 'business', 'technical'].includes(tab) ? tab : null,
    bots: bots && bots.length > 0 ? bots : null,
  };
}

function writeQuery({ from, to, tab, bots }) {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  if (from) params.set('from', from); else params.delete('from');
  if (to)   params.set('to',   to);   else params.delete('to');
  if (tab)  params.set('tab',  tab);  else params.delete('tab');
  if (Array.isArray(bots) && bots.length > 0) params.set('bots', bots.join(','));
  else params.delete('bots');
  const next = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}${window.location.hash}`;
  window.history.replaceState(null, '', next);
}

function isDatetimeLocal(s) {
  return typeof s === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s);
}
